import { gql } from '@apollo/client';

/**
 * Per-call Apollo context carrying a fresh `Idempotency-Key`. Every clinical mutation
 * is `@Idempotent()` on the server, which requires this header — same pattern the
 * cashier charge flow uses. A new key per invocation is correct: each save/add is a
 * distinct write, and a retried request reuses nothing.
 */
function makeUuid(): string {
  const c: Crypto | undefined = globalThis.crypto;
  // `crypto.randomUUID` is secure-context-only; a plain-HTTP LAN deploy would throw.
  // `getRandomValues` has no such restriction — fall back to a manual RFC-4122 v4.
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const b = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(b);
  else for (let i = 0; i < 16; i += 1) b[i] = Math.floor(Math.random() * 256);
  b[6] = ((b[6] ?? 0) & 0x0f) | 0x40;
  b[8] = ((b[8] ?? 0) & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0'));
  return `${h[0]}${h[1]}${h[2]}${h[3]}-${h[4]}${h[5]}-${h[6]}${h[7]}-${h[8]}${h[9]}-${h[10]}${h[11]}${h[12]}${h[13]}${h[14]}${h[15]}`;
}

export function idempotencyContext(): { headers: { 'Idempotency-Key': string } } {
  return { headers: { 'Idempotency-Key': makeUuid() } };
}

export const CLINICAL_VISIT_FIELDS = gql`
  fragment ClinicalVisitFields on ClinicalVisit {
    id
    folio_number
    status
    visit_kind
    pet_id
    pet_record_id
    sale_order_id
    veterinarian_id
    consulting_room_id
    consulting_room_snapshot_json
    chief_complaint
    weight_grams_at_visit
    started_at
    completed_at
    closed_at
    closed_by
    addended_at
    cancelled_at
    voided_at
    created_at
    updated_at
  }
`;

export const CLINICAL_ANAMNESIS_FIELDS = gql`
  fragment ClinicalAnamnesisFields on ClinicalAnamnesis {
    id
    clinical_visit_id
    chief_complaint
    symptom_onset_days
    previous_illnesses
    current_medications
    vaccination_status
    deworming_status
    diet_notes
    environment_notes
    other_notes
  }
`;

export const CLINICAL_EXAM_FIELDS = gql`
  fragment ClinicalExamFields on ClinicalPhysicalExam {
    id
    clinical_visit_id
    body_weight_grams
    body_temperature_celsius_tenths
    heart_rate_bpm
    respiratory_rate_bpm
    mucous_membranes
    hydration_status
    consciousness_level
    body_condition_score
    musculoskeletal_notes
    cardiovascular_notes
    respiratory_notes
    gastrointestinal_notes
    genitourinary_notes
    neurological_notes
    integumentary_notes
    lymph_node_notes
    abdomen_notes
    exam_not_performed_reason
    general_impression
  }
`;

export const CLINICAL_PLAN_FIELDS = gql`
  fragment ClinicalPlanFields on ClinicalPlanTreatment {
    id
    clinical_visit_id
    treatment_notes
    medications_notes
    diet_recommendations
    activity_restrictions
    follow_up_notes
    owner_instructions
  }
`;

export const CLINICAL_DIAGNOSIS_FIELDS = gql`
  fragment ClinicalDiagnosisFields on ClinicalDiagnosis {
    id
    clinical_visit_id
    display_order
    is_primary
    diagnosis_kind
    icd_code
    description
    prognosis
    notes
  }
`;

export const CLINICAL_ATTACHMENT_FIELDS = gql`
  fragment ClinicalAttachmentFields on ClinicalAttachment {
    id
    pet_record_id
    clinical_visit_id
    kind
    title
    original_filename
    content_type
    size_bytes
    stored_size_bytes
    compression
    content_hash
    status
    uploaded_by
    created_at
  }
`;

export const CLINICAL_ATTACHMENTS_QUERY = gql`
  ${CLINICAL_ATTACHMENT_FIELDS}
  query ClinicalAttachments($visit_id: ID, $pet_record_id: ID, $kind: String) {
    clinicalAttachments(visit_id: $visit_id, pet_record_id: $pet_record_id, kind: $kind) {
      ...ClinicalAttachmentFields
    }
  }
`;

export const REGISTER_CLINICAL_ATTACHMENT_MUTATION = gql`
  ${CLINICAL_ATTACHMENT_FIELDS}
  mutation RegisterClinicalAttachment($input: RegisterClinicalAttachmentInput!) {
    registerClinicalAttachment(input: $input) {
      ...ClinicalAttachmentFields
    }
  }
`;

export const ARCHIVE_CLINICAL_ATTACHMENT_MUTATION = gql`
  ${CLINICAL_ATTACHMENT_FIELDS}
  mutation ArchiveClinicalAttachment($id: ID!) {
    archiveClinicalAttachment(id: $id) {
      ...ClinicalAttachmentFields
    }
  }
`;

export const CLINICAL_ADDENDUM_FIELDS = gql`
  fragment ClinicalAddendumFields on ClinicalAddendum {
    id
    clinical_visit_id
    content
    created_by
    created_at
  }
`;

export const LAB_REQUEST_FIELDS = gql`
  fragment LabRequestFields on LabRequest {
    id
    clinical_visit_id
    lab_name
    panel_name
    status
    notes
    created_at
  }
`;

export const LAB_RESULT_FIELDS = gql`
  fragment LabResultFields on LabResult {
    id
    lab_request_id
    test_name
    result_value
    unit
    reference_range
    is_abnormal
    interpretation
    attachment_id
    created_at
  }
`;

export const IMAGING_STUDY_FIELDS = gql`
  fragment ImagingStudyFields on ImagingStudy {
    id
    clinical_visit_id
    study_kind
    body_region
    findings
    interpretation
    attachment_id
    performed_at
    created_at
  }
`;

export const CLINICAL_LAB_REQUESTS_QUERY = gql`
  ${LAB_REQUEST_FIELDS}
  query ClinicalLabRequests($visit_id: ID!) {
    clinicalLabRequests(visit_id: $visit_id) {
      ...LabRequestFields
    }
  }
`;

export const CLINICAL_LAB_RESULTS_QUERY = gql`
  ${LAB_RESULT_FIELDS}
  query ClinicalLabResults($lab_request_id: ID!) {
    clinicalLabResults(lab_request_id: $lab_request_id) {
      ...LabResultFields
    }
  }
`;

export const CLINICAL_IMAGING_STUDIES_QUERY = gql`
  ${IMAGING_STUDY_FIELDS}
  query ClinicalImagingStudies($visit_id: ID!) {
    clinicalImagingStudies(visit_id: $visit_id) {
      ...ImagingStudyFields
    }
  }
`;

export const CREATE_LAB_REQUEST_MUTATION = gql`
  ${LAB_REQUEST_FIELDS}
  mutation CreateLabRequest($input: CreateLabRequestInput!) {
    createLabRequest(input: $input) {
      ...LabRequestFields
    }
  }
`;

export const UPDATE_LAB_REQUEST_STATUS_MUTATION = gql`
  ${LAB_REQUEST_FIELDS}
  mutation UpdateLabRequestStatus($input: UpdateLabRequestStatusInput!) {
    updateLabRequestStatus(input: $input) {
      ...LabRequestFields
    }
  }
`;

export const RECORD_LAB_RESULT_MUTATION = gql`
  ${LAB_RESULT_FIELDS}
  mutation RecordLabResult($input: RecordLabResultInput!) {
    recordLabResult(input: $input) {
      ...LabResultFields
    }
  }
`;

export const REGISTER_IMAGING_STUDY_MUTATION = gql`
  ${IMAGING_STUDY_FIELDS}
  mutation RegisterImagingStudy($input: RegisterImagingStudyInput!) {
    registerImagingStudy(input: $input) {
      ...ImagingStudyFields
    }
  }
`;

export const INTERPRET_IMAGING_STUDY_MUTATION = gql`
  ${IMAGING_STUDY_FIELDS}
  mutation InterpretImagingStudy($input: InterpretImagingStudyInput!) {
    interpretImagingStudy(input: $input) {
      ...ImagingStudyFields
    }
  }
`;

export const PRESCRIPTION_FIELDS = gql`
  fragment PrescriptionFields on ClinicalPrescription {
    id
    clinical_visit_id
    status
    notes
    discontinued_reason
    created_at
  }
`;

export const PRESCRIPTION_LINE_FIELDS = gql`
  fragment PrescriptionLineFields on ClinicalPrescriptionLine {
    id
    prescription_id
    display_order
    product_id
    drug_name
    concentration
    dose
    dose_unit
    route
    frequency
    duration_days
    instructions
    is_controlled
  }
`;

export const CLINICAL_PRESCRIPTIONS_QUERY = gql`
  ${PRESCRIPTION_FIELDS}
  query ClinicalPrescriptions($visit_id: ID!) {
    clinicalPrescriptions(visit_id: $visit_id) {
      ...PrescriptionFields
    }
  }
`;

export const CLINICAL_PRESCRIPTION_LINES_QUERY = gql`
  ${PRESCRIPTION_LINE_FIELDS}
  query ClinicalPrescriptionLines($prescription_id: ID!) {
    clinicalPrescriptionLines(prescription_id: $prescription_id) {
      ...PrescriptionLineFields
    }
  }
`;

export const PET_VITALS_HISTORY_QUERY = gql`
  query PetVitalsHistory($pet_id: ID!) {
    petVitalsHistory(pet_id: $pet_id) {
      visit_id
      folio_number
      measured_at
      visit_kind
      weight_grams
      weight_source
      temperature_celsius_tenths
      heart_rate_bpm
      respiratory_rate_bpm
      body_condition_score
    }
  }
`;

export const CREATE_PRESCRIPTION_MUTATION = gql`
  ${PRESCRIPTION_FIELDS}
  mutation CreateClinicalPrescription($input: CreateClinicalPrescriptionInput!) {
    createClinicalPrescription(input: $input) {
      ...PrescriptionFields
    }
  }
`;

export const ADD_PRESCRIPTION_LINE_MUTATION = gql`
  ${PRESCRIPTION_LINE_FIELDS}
  mutation AddPrescriptionLine($input: AddPrescriptionLineInput!) {
    addPrescriptionLine(input: $input) {
      ...PrescriptionLineFields
    }
  }
`;

export const REMOVE_PRESCRIPTION_LINE_MUTATION = gql`
  mutation RemovePrescriptionLine($line_id: ID!) {
    removePrescriptionLine(line_id: $line_id)
  }
`;

export const ISSUE_PRESCRIPTION_MUTATION = gql`
  ${PRESCRIPTION_FIELDS}
  mutation IssuePrescription($prescription_id: ID!) {
    issuePrescription(prescription_id: $prescription_id) {
      ...PrescriptionFields
    }
  }
`;

export const DISCONTINUE_PRESCRIPTION_MUTATION = gql`
  ${PRESCRIPTION_FIELDS}
  mutation DiscontinuePrescription($input: DiscontinuePrescriptionInput!) {
    discontinuePrescription(input: $input) {
      ...PrescriptionFields
    }
  }
`;

export const COMPLETE_PRESCRIPTION_MUTATION = gql`
  ${PRESCRIPTION_FIELDS}
  mutation CompletePrescription($prescription_id: ID!) {
    completePrescription(prescription_id: $prescription_id) {
      ...PrescriptionFields
    }
  }
`;

export const CLINICAL_VISIT_BY_ORDER_QUERY = gql`
  ${CLINICAL_VISIT_FIELDS}
  ${CLINICAL_ANAMNESIS_FIELDS}
  ${CLINICAL_EXAM_FIELDS}
  ${CLINICAL_PLAN_FIELDS}
  ${CLINICAL_DIAGNOSIS_FIELDS}
  ${CLINICAL_ADDENDUM_FIELDS}
  query ClinicalVisitByOrderId($sale_order_id: ID!) {
    clinicalVisitByOrderId(sale_order_id: $sale_order_id) {
      visit {
        ...ClinicalVisitFields
      }
      anamnesis {
        ...ClinicalAnamnesisFields
      }
      physical_exam {
        ...ClinicalExamFields
      }
      diagnoses {
        ...ClinicalDiagnosisFields
      }
      plan_treatment {
        ...ClinicalPlanFields
      }
      addendums {
        ...ClinicalAddendumFields
      }
    }
  }
`;

export const CLINICAL_VISIT_DETAIL_QUERY = gql`
  ${CLINICAL_VISIT_FIELDS}
  ${CLINICAL_ANAMNESIS_FIELDS}
  ${CLINICAL_EXAM_FIELDS}
  ${CLINICAL_PLAN_FIELDS}
  ${CLINICAL_DIAGNOSIS_FIELDS}
  ${CLINICAL_ADDENDUM_FIELDS}
  query ClinicalVisit($id: ID!) {
    clinicalVisit(id: $id) {
      visit {
        ...ClinicalVisitFields
      }
      anamnesis {
        ...ClinicalAnamnesisFields
      }
      physical_exam {
        ...ClinicalExamFields
      }
      diagnoses {
        ...ClinicalDiagnosisFields
      }
      plan_treatment {
        ...ClinicalPlanFields
      }
      addendums {
        ...ClinicalAddendumFields
      }
    }
  }
`;

// Read-only hospitalization stays for the pet's clinical-history timeline (episodes are a
// separate care/billing entity, surfaced here for visibility + deep-linked to the board).
export const PET_HOSPITALIZATIONS_QUERY = gql`
  query PetHospitalizations($pet_id: ID!) {
    hospitalizationEpisodes(pet_id: $pet_id) {
      id
      folio_number
      status
      admission_reason
      location_label
      admitted_at
      discharged_at
    }
  }
`;

export const CLINICAL_RECORD_QUERY = gql`
  ${CLINICAL_VISIT_FIELDS}
  query ClinicalRecord($pet_id: ID!) {
    clinicalRecord(pet_id: $pet_id) {
      pet_record_id
      code
      pet_id
      status
      visit_count
      open_visit_count
      last_visit_at
      last_visit_status
      visits {
        ...ClinicalVisitFields
      }
    }
  }
`;

export const PET_CLINICAL_SUMMARY_QUERY = gql`
  query PetClinicalSummary($pet_id: ID!) {
    petClinicalSummary(pet_id: $pet_id) {
      pet_id
      pet_name
      species
      pet_record_code
      visit_count
      open_visit_count
      last_visit_at
      last_visit_status
      last_weight_grams_at_visit
    }
  }
`;

export const MY_PENDING_CLINICAL_VISITS_QUERY = gql`
  ${CLINICAL_VISIT_FIELDS}
  query MyPendingClinicalVisits($offset: Int, $limit: Int) {
    myPendingClinicalVisits(offset: $offset, limit: $limit) {
      items {
        ...ClinicalVisitFields
      }
      page {
        total
        offset
        limit
        has_more
      }
    }
  }
`;

export const SAVE_CLINICAL_ANAMNESIS_MUTATION = gql`
  ${CLINICAL_ANAMNESIS_FIELDS}
  mutation SaveClinicalAnamnesis($input: SaveClinicalAnamnesisInput!) {
    saveClinicalAnamnesis(input: $input) {
      ...ClinicalAnamnesisFields
    }
  }
`;

export const SAVE_CLINICAL_PHYSICAL_EXAM_MUTATION = gql`
  ${CLINICAL_EXAM_FIELDS}
  mutation SaveClinicalPhysicalExam($input: SaveClinicalPhysicalExamInput!) {
    saveClinicalPhysicalExam(input: $input) {
      ...ClinicalExamFields
    }
  }
`;

export const SAVE_CLINICAL_PLAN_TREATMENT_MUTATION = gql`
  ${CLINICAL_PLAN_FIELDS}
  mutation SaveClinicalPlanTreatment($input: SaveClinicalPlanTreatmentInput!) {
    saveClinicalPlanTreatment(input: $input) {
      ...ClinicalPlanFields
    }
  }
`;

export const ADD_CLINICAL_DIAGNOSIS_MUTATION = gql`
  ${CLINICAL_DIAGNOSIS_FIELDS}
  mutation AddClinicalDiagnosis($input: AddClinicalDiagnosisInput!) {
    addClinicalDiagnosis(input: $input) {
      ...ClinicalDiagnosisFields
    }
  }
`;

export const UPDATE_CLINICAL_DIAGNOSIS_MUTATION = gql`
  ${CLINICAL_DIAGNOSIS_FIELDS}
  mutation UpdateClinicalDiagnosis($id: ID!, $input: UpdateClinicalDiagnosisInput!) {
    updateClinicalDiagnosis(id: $id, input: $input) {
      ...ClinicalDiagnosisFields
    }
  }
`;

export const REMOVE_CLINICAL_DIAGNOSIS_MUTATION = gql`
  mutation RemoveClinicalDiagnosis($id: ID!) {
    removeClinicalDiagnosis(id: $id)
  }
`;

export const CLOSE_CLINICAL_VISIT_MUTATION = gql`
  ${CLINICAL_VISIT_FIELDS}
  mutation CloseClinicalVisit($input: CloseClinicalVisitInput!) {
    closeClinicalVisit(input: $input) {
      ...ClinicalVisitFields
    }
  }
`;

export const MARK_CLINICAL_VISIT_PENDING_COMPLETION_MUTATION = gql`
  ${CLINICAL_VISIT_FIELDS}
  mutation MarkClinicalVisitPendingCompletion($id: ID!) {
    markClinicalVisitPendingCompletion(id: $id) {
      ...ClinicalVisitFields
    }
  }
`;

export const ADD_CLINICAL_ADDENDUM_MUTATION = gql`
  ${CLINICAL_ADDENDUM_FIELDS}
  mutation AddClinicalAddendum($input: AddClinicalAddendumInput!) {
    addClinicalAddendum(input: $input) {
      ...ClinicalAddendumFields
    }
  }
`;

export const UPDATE_CLINICAL_VISIT_CHIEF_COMPLAINT_MUTATION = gql`
  ${CLINICAL_VISIT_FIELDS}
  mutation UpdateClinicalVisitChiefComplaint($input: UpdateClinicalVisitChiefComplaintInput!) {
    updateClinicalVisitChiefComplaint(input: $input) {
      ...ClinicalVisitFields
    }
  }
`;
